/**
 * Oroboros in-app router (landing vs terminal vs docs vs 404).
 * Dedicated host: `/` landing, `/app` terminal. Legacy `/oroboros/*` still resolves.
 *
 * Every view but the landing is a lazy chunk, so a first visit to `/` downloads React, the
 * landing and its CSS — not the terminal, CodeMirror, web3-onboard or the chart engine. Vite
 * splits the CSS the same way (`build.cssCodeSplit`), so the terminal stylesheet only loads on
 * `/app`. The landing stays a static import because it is the entry most visitors hit first.
 */

import React, { Suspense, lazy, useEffect, useMemo } from 'react';
import { OroborosLanding } from './OroborosLanding';
import { OroborosErrorBoundary } from './OroborosErrorBoundary';
import { canonicalOroborosHref, resolveOroborosView as resolveView } from './routes';

export { resolveOroborosView } from './routes';

const OroborosTerminal = lazy(() =>
  import('./terminal/OroborosTerminal').then((m) => ({ default: m.OroborosTerminal })),
);
const OroborosDocsPage = lazy(() =>
  import('./docs/OroborosDocsPage').then((m) => ({ default: m.OroborosDocsPage })),
);
const OroborosArtificeCallback = lazy(() =>
  import('./OroborosArtificeCallback').then((m) => ({ default: m.OroborosArtificeCallback })),
);
const OroborosBrandingPage = lazy(() =>
  import('./branding/OroborosBrandingPage').then((m) => ({ default: m.OroborosBrandingPage })),
);
const OroborosNotFound = lazy(() =>
  import('./OroborosNotFound').then((m) => ({ default: m.OroborosNotFound })),
);

/** Ground-coloured placeholder while a chunk loads: no white flash, no spinner from the kernel. */
const ChunkFallback: React.FC = () => <div className="oro-chunk-fallback" aria-hidden="true" />;

export const OroborosApp: React.FC = () => {
  const view = useMemo(() => {
    try {
      return resolveView(window.location.pathname || '');
    } catch {
      return 'landing';
    }
  }, []);

  useEffect(() => {
    try {
      const next = canonicalOroborosHref(
        window.location.pathname || '',
        window.location.search || '',
        window.location.hash || '',
      );
      if (next) window.history.replaceState({}, '', next);
    } catch {
      /* ignore */
    }
  }, []);

  // Warm the terminal chunk from the landing once the page is idle, so "Launch Oroboros" is instant.
  useEffect(() => {
    if (view !== 'landing') return;
    const w = window as Window & {
      requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
      cancelIdleCallback?: (id: number) => void;
    };
    const run = () => {
      void import('./terminal/OroborosTerminal');
    };
    if (typeof w.requestIdleCallback === 'function') {
      const id = w.requestIdleCallback(run, { timeout: 4000 });
      return () => w.cancelIdleCallback?.(id);
    }
    const id = window.setTimeout(run, 2500);
    return () => window.clearTimeout(id);
  }, [view]);

  if (view === 'landing') return <OroborosLanding />;

  let page: React.ReactNode;
  if (view === 'terminal') {
    page = (
      <OroborosErrorBoundary label="OroborosTerminal">
        <OroborosTerminal />
      </OroborosErrorBoundary>
    );
  } else if (view === 'docs') {
    page = <OroborosDocsPage />;
  } else if (view === 'auth-callback') {
    page = <OroborosArtificeCallback />;
  } else if (view === 'branding') {
    page = <OroborosBrandingPage />;
  } else {
    page = <OroborosNotFound />;
  }

  return <Suspense fallback={<ChunkFallback />}>{page}</Suspense>;
};

export default OroborosApp;
